(function () {
  'use strict';
  if (customElements.get('desky-product-accessories')) return;
  const core = window.DeskyAccessoriesCore;
  class ProductAccessories extends HTMLElement {
    static get observedAttributes() { return ['data-price-display', 'data-sold-out-accessories']; }
    attributeChangedCallback() {
      if (this.controller && this.data && this.form) this.render();
    }
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = document.getElementById(this.dataset.formId);
      this.section = this.closest('.shopify-section');
      this.error = this.querySelector('[data-accessory-error]');
      this.status = this.querySelector('[data-accessory-status]');
      this.selections = Object.create(null);
      this.confirmations = new Set();
      this.data = JSON.parse(this.querySelector('[data-accessory-data]').textContent);
      // The product's variants come from the section's one ProductVariantsJson copy (Q-275 fix 2).
      this.data.product.variants ||= JSON.parse(document.getElementById('ProductVariantsJson-' + this.dataset.sectionId)?.textContent || '[]');
      this.data.error ||= this.data.limitError;
      // Liquid's t filter HTML-escapes the strings (qu'il -> qu&#39;il) before | json; they are shown via
      // textContent, so decode them once here or the entities appear literally (e.g. FR soldOutAccessories).
      const decodeEntities = (value) => typeof value === 'string' && value.includes('&')
        ? new DOMParser().parseFromString(value, 'text/html').documentElement.textContent
        : value;
      this.strings = Object.fromEntries(Object.entries(this.data.strings || {}).map(([key, value]) => [key, decodeEntities(value)]));
      this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency });
      if (!this.form) { this.showError(this.strings.configuration); return; }
      this.onChange = event => {
        const groupId = event.target.dataset.accessoryGroup;
        if (groupId) {
          this.selections[groupId] = event.target.value || null;
          this.confirmations.delete(groupId);
          this.showError('');
          this.render(event.target.id);
        } else if (event.target.name === 'id' || event.target.name === 'quantity') this.refresh();
      };
      this.form.addEventListener('change', this.onChange, options);
      this.form.addEventListener('submit', event => {
        event.preventDefault(); event.stopImmediatePropagation(); this.submit();
      }, { ...options, capture: true });
      this.addEventListener('change', event => {
        // Movable blocks may sit outside the form; handle their input changes once.
        if (!this.form.contains(this)) this.onChange(event);
      }, options);
      this.section.addEventListener('click', event => this.handleInstalmentClick(event), { ...options, capture: true });
      this.section.addEventListener('desky:variant-change', event => {
        this.refresh(event.detail.variant, true);
      }, options);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-accessory-confirm]');
        if (button) {
          this.confirmations.delete(button.dataset.accessoryConfirm);
          this.render();
        }
      }, options);
      this.tooltip = document.createElement('div');
      this.tooltip.className = 'desky-accessories__tooltip';
      this.tooltip.id = 'AccessoryTooltip-' + this.dataset.sectionId;
      this.tooltip.setAttribute('role', 'tooltip');
      this.tooltip.hidden = true;
      this.append(this.tooltip);
      this.tooltip.addEventListener('pointerenter', () => clearTimeout(this.tooltipTimer), options);
      this.tooltip.addEventListener('pointerleave', () => this.scheduleTooltipClose(), options);
      document.addEventListener('pointerdown', event => {
        if (!this.tooltip.contains(event.target) && !this.tooltipTrigger?.contains(event.target)) this.closeTooltip();
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') { this.closeTooltip(); this.closeInstalments(); }
      }, options);
      document.addEventListener('scroll', () => this.closeTooltip(), { ...options, capture: true });
      window.addEventListener('resize', () => this.closeTooltip(), options);
      this.refresh();
    }
    openTooltip(button, content) {
      clearTimeout(this.tooltipTimer);
      if (this.tooltipTrigger !== button) this.closeTooltip();
      this.tooltipTrigger = button;
      this.tooltip.textContent = content;
      this.tooltip.hidden = false;
      button.setAttribute('aria-describedby', this.tooltip.id);
      const anchor = button.getBoundingClientRect();
      const box = this.tooltip.getBoundingClientRect();
      const width = document.documentElement.clientWidth;
      const height = document.documentElement.clientHeight;
      this.tooltip.style.left = Math.max(8, Math.min(anchor.left + anchor.width / 2 - box.width / 2, width - box.width - 8)) + 'px';
      const top = anchor.bottom + box.height + 8 <= height ? anchor.bottom + 8 : anchor.top - box.height - 8;
      this.tooltip.style.top = Math.max(8, top) + 'px';
    }
    scheduleTooltipClose() {
      clearTimeout(this.tooltipTimer);
      this.tooltipTimer = setTimeout(() => {
        if (!this.tooltipPinned && document.activeElement !== this.tooltipTrigger) this.closeTooltip();
      }, 150);
    }
    closeTooltip() {
      clearTimeout(this.tooltipTimer);
      this.tooltipTrigger?.removeAttribute('aria-describedby');
      this.tooltipTrigger = null;
      this.tooltipPinned = false;
      if (this.tooltip) this.tooltip.hidden = true;
    }
    disconnectedCallback() { this.closeInstalments(); this.releaseMainPrice(); this.closeTooltip(); this.tooltip?.remove(); this.controller?.abort(); this.controller = null; }
    format(price) { return this.money.format(price / 100); }
    moneyMarkup(cents) {
      return typeof window.formatMoney === 'function' ? window.formatMoney(cents) : this.format(cents);
    }
    setMoney(element, cents) {
      const markup = cents === null ? '—' : this.moneyMarkup(cents);
      if (element.innerHTML !== markup) element.innerHTML = markup;
    }
    closeInstalments() {
      if (!this.instalmentTrigger) return;
      this.section.querySelector('.instalment-popup')?.classList.remove('active', 'zip-popup');
      this.section.querySelector('.instalment-overlay-popup')?.classList.remove('active');
      this.instalmentTrigger.focus();
      this.instalmentTrigger = null;
    }
    handleInstalmentClick(event) {
      if (!this.priceTarget) return;
      const trigger = event.target.closest('.logos [data-provider]');
      if (trigger) {
        const provider = trigger.dataset.provider;
        const source = this.section.querySelector('.' + CSS.escape(provider) + '-popup-content');
        const popup = this.section.querySelector('.instalment-popup');
        const content = popup?.querySelector('.content-popup');
        if (!source || !content) return;
        event.preventDefault(); event.stopImmediatePropagation();
        content.innerHTML = source.innerHTML;
        popup.classList.toggle('zip-popup', provider === 'zip');
        if (provider === 'zip' && typeof window.initReadMore === 'function') window.initReadMore(content);
        popup.classList.add('active');
        this.section.querySelector('.instalment-overlay-popup')?.classList.add('active');
        this.instalmentTrigger = trigger;
      } else if (event.target.closest('.instalment-popup .close, .instalment-overlay-popup')) {
        event.preventDefault(); event.stopImmediatePropagation(); this.closeInstalments();
      }
    }
    updateInstalments(total) {
      this.section.querySelectorAll('[data-instalment-counts]').forEach(plans => {
        const counts = JSON.parse(plans.dataset.instalmentCounts).filter(n => Number.isSafeInteger(n) && n > 0);
        const summary = plans.querySelector('[data-instalment-summary]');
        if (summary) summary.hidden = total === null || !counts.length;
        const amount = plans.querySelector('.price');
        if (amount) this.setMoney(amount, total !== null && counts.length ? Math.round(total / Math.max(...counts)) : null);
      });
      // Includes hidden provider templates and their currently open popup copies.
      this.section.querySelectorAll('[data-instalment-count]').forEach(amount => {
        const count = Number(amount.dataset.instalmentCount);
        this.setMoney(amount, total !== null && Number.isSafeInteger(count) && count > 0 ? Math.round(total / count) : null);
      });
    }
    writeMainPrice(target, total, compare) {
      let amount = target.querySelector('.bold_option_price_display');
      if (!amount) {
        amount = document.createElement('span'); amount.className = 'bold_option_price_display'; target.append(amount);
      }
      this.setMoney(amount, total);
      let previous = target.querySelector('del');
      let badge = target.querySelector('.discount-display-wrapper');
      if (compare !== null && total !== null && compare > total) {
        if (!previous) { previous = document.createElement('del'); target.prepend(previous); }
        previous.dataset.compare = String(compare); previous.dataset.price = String(total);
        this.setMoney(previous, compare);
        if (!badge) { badge = document.createElement('span'); badge.className = 'discount-display-wrapper'; target.append(badge); }
        const saving = compare - total;
        const percent = Math.round(saving * 100 / compare);
        const majorSaving = Math.round(saving / 100);
        const useMoney = !(percent > 40 && majorSaving < 50) && (majorSaving >= 100 || percent < 10 || saving % 500 === 0);
        const value = useMoney ? this.moneyMarkup(saving) : percent + '%';
        const markup = '<span class="discount-display" style="color:#d10300;font-size:14px;margin-left:5px" data-discount-amount="' + saving + '" data-discount-percent="' + percent + '">' + value + ' Off</span>';
        if (badge.innerHTML !== markup) badge.innerHTML = markup;
      } else {
        previous?.remove();
        if (badge) badge.replaceChildren();
      }
      this.updateInstalments(total);
      // Q-316 Omnibus: the note names the variant's own "was" price, without accessories
      if (window.deskyOmnibusNote) {
        const was = this.variant?.compare_at_price > this.variant?.price ? this.variant.compare_at_price : 0;
        window.deskyOmnibusNote(was);
      }
      const priceText = amount.textContent.trim();
      this.section.querySelectorAll('[data-product-price]').forEach(el => { el.textContent = priceText; });
      document.querySelectorAll('[data-product-price][data-price-section]').forEach(el => {
        if (el.dataset.priceSection === this.dataset.sectionId) el.textContent = priceText;
      });
    }
    releaseMainPrice() {
      if (!this.priceTarget) return;
      const target = this.priceTarget;
      if (target.isConnected) this.writeMainPrice(target, this.variant?.price ?? null, this.variant?.compare_at_price ?? null);
      delete target.dataset.deskyPriceManaged;
      this.priceTarget = null;
    }
    syncMainPrice(total) {
      const target = this.form.querySelector('.price-one');
      if (this.dataset.priceDisplay !== 'main' || !target) {
        this.releaseMainPrice();
        return false;
      }
      this.priceTarget = target;
      target.dataset.deskyPriceManaged = 'true';
      const errors = core.validate(this.data, this.variant, this.selections);
      // Keep the configured total visible for sold-out desks; only clear when the
      // combination itself cannot be priced (missing variant, bad config, unavailable accessory).
      const priceKnown = !!this.variant && !this.data.error && !errors.some(e => ['configuration', 'unavailable'].includes(e.message));
      const amount = priceKnown ? total : null;
      const compare = priceKnown && this.variant.compare_at_price > this.variant.price
        ? this.variant.compare_at_price + total - this.variant.price : null;
      this.writeMainPrice(target, amount, compare);
      return true;
    }
    currentNativeVariant() {
      const picker = this.section.querySelector('variant-selects,variant-radios');
      if (!picker) return null;
      const selects = [...picker.querySelectorAll('select')];
      const selectedValues = selects.length === this.data.product.options.length
        ? selects.map(select => select.value) : [...picker.querySelectorAll('input[type="radio"]:checked')].map(input => input.value);
      if (selectedValues.length === this.data.product.options.length && selectedValues.every(Boolean)) {
        const selected = this.data.product.variants.find(variant => variant.options.every((value, index) => value === selectedValues[index]));
        if (selected) return selected;
      }
      return picker.currentVariant || null;
    }
    isProductSoldOut() { return !this.data.product.variants.some(variant => variant.available); }
    refresh(nextVariant, explicit = false) {
      const formVariant = this.data.product.variants.find(v => String(v.id) === this.form.elements.id.value);
      let next = explicit ? nextVariant : formVariant || this.currentNativeVariant();
      // Shopify leaves the hidden variant ID blank when every variant is sold out.
      // Use the visible/default configuration only to show disabled, informative choices.
      if (!next && this.isProductSoldOut() && this.dataset.soldOutAccessories !== 'hide') next = this.data.product.variants[0] || null;
      const old = this.variant;
      if (old && next && old.id !== next.id) {
        for (const group of this.data.groups) {
          const choice = group.choices.find(c => c.id === this.selections[group.id]);
          if (!choice) continue;
          const before = core.resolve(choice, this.data.product, old).variant;
          const after = core.resolve(choice, this.data.product, next).variant;
          if (before && after && after.price > before.price) this.confirmations.add(group.id);
        }
      }
      this.variant = next || null;
      this.render();
      const button = this.form.querySelector('[name="add"]');
      if (button) button.disabled = !this.variant?.available || this.busy || this.locked || this.data.error;
      this.form.dataset.accessoriesReady = this.data.error ? 'false' : 'true';
      this.form.elements.id.disabled = Boolean(this.data.error || !this.variant);
      if (old && old.id !== next?.id) this.status.textContent = this.strings.updated;
    }
    showError(text) { this.error.textContent = text; this.error.hidden = !text; }
    render(focusId) {
      this.closeTooltip();
      const container = this.querySelector('[data-accessory-groups]');
      container.replaceChildren();
      const productSoldOut = this.isProductSoldOut();
      const showSoldOutAccessories = this.dataset.soldOutAccessories !== 'hide';
      this.hidden = productSoldOut && !showSoldOutAccessories;
      if (this.hidden) {
        this.querySelector('.desky-accessories__total').hidden = true;
        this.releaseMainPrice();
        return;
      }
      let total = this.variant?.price || 0;
      for (const group of this.data.groups) {
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'desky-accessories__group';
        fieldset.disabled = Boolean(this.busy || this.locked || productSoldOut);
        const legend = document.createElement('legend');
        legend.textContent = group.heading + (group.required ? ' *' : '');
        fieldset.append(legend);
        if (group.help) { const help = document.createElement('p'); help.textContent = group.help; fieldset.append(help); }
        if (productSoldOut) {
          const message = document.createElement('p');
          message.className = 'desky-accessories__sold-out';
          message.textContent = this.strings.soldOutAccessories;
          fieldset.append(message);
        }
        const entries = [];
        if (group.allowNone) entries.push({ id: 'none', label: group.noneLabel, title: group.noneLabel, price: '', available: true });
        for (const choice of group.choices) {
          const resolved = core.resolve(choice, this.data.product, this.variant);
          if (!resolved.eligible && this.selections[group.id] !== choice.id) continue;
          const variant = resolved.variant;
          const finish = group.showVariantDetails !== false && variant && variant.title !== 'Default Title' ? ' — ' + variant.title : '';
          const title = choice.label + finish;
          const price = variant ? '(+' + this.format(variant.price) + ')' : '';
          entries.push({
            id: choice.id, title, price, label: title + (price ? ' ' + price : ''),
            image: choice.image || variant?.image, available: !productSoldOut && resolved.eligible && resolved.available
          });
          if (this.selections[group.id] === choice.id && resolved.eligible) total += variant.price;
        }
        if (group.presentation === 'dropdown') {
          const select = document.createElement('select');
          select.id = 'Accessory-' + this.dataset.sectionId + '-' + group.id;
          select.setAttribute('form', this.dataset.formId);
          select.dataset.accessoryGroup = group.id;
          select.setAttribute('aria-label', group.heading);
          select.setAttribute('aria-required', String(group.required));
          select.add(new Option(this.strings.choose, ''));
          entries.forEach(entry => {
            const option = new Option(entry.label + (!entry.available ? ' — ' + this.strings.unavailableLabel : ''), entry.id);
            option.disabled = !entry.available;
            select.add(option);
          });
          select.value = this.selections[group.id] || '';
          fieldset.append(select);
        } else {
          const row = document.createElement('div');
          row.className = 'desky-accessories__images';
          entries.forEach(entry => {
            const card = document.createElement('div');
            card.className = 'desky-accessories__card';
            const label = document.createElement('label');
            label.className = 'desky-accessories__choice';
            const input = document.createElement('input');
            input.type = 'radio'; input.name = 'accessory-' + this.dataset.sectionId + '-' + group.id;
            input.setAttribute('form', this.dataset.formId);
            input.id = input.name + '-' + entry.id; input.value = entry.id;
            input.dataset.accessoryGroup = group.id;
            input.checked = this.selections[group.id] === entry.id;
            input.disabled = !entry.available;
            input.setAttribute('aria-label', entry.label + (!entry.available ? ' — ' + this.strings.unavailableLabel : ''));
            if (group.showRadioIndicator === false) input.className = 'desky-accessories__radio-hidden';
            input.addEventListener('click', () => {
              if (!group.required && this.selections[group.id] === entry.id) {
                this.selections[group.id] = null;
                this.confirmations.delete(group.id);
                this.render(input.id);
              }
            });
            label.append(input);
            let media;
            if (entry.image) {
              const image = document.createElement('img'); image.src = entry.image; image.alt = ''; image.width = 96; image.height = 96;
              media = document.createElement('span'); media.className = 'desky-accessories__media';
              media.append(image); label.append(media);
            }
            const tooltipParts = [];
            // A no-accessory card always needs its visible label, even without an image.
            const tooltipLabel = group.labelDisplay === 'tooltip' && entry.id !== 'none' && entry.image;
            const tooltipPrice = group.priceDisplay === 'tooltip';
            for (const [content, inTooltip, className] of [
              [entry.title, tooltipLabel, 'desky-accessories__label'],
              [entry.price, tooltipPrice, 'desky-accessories__price']
            ]) {
              if (!content) continue;
              if (inTooltip) tooltipParts.push(content);
              else {
                const text = document.createElement('span');
                text.className = className; text.textContent = content; label.append(text);
              }
            }
            if (!entry.available) {
              const badge = document.createElement('span'); badge.className = 'desky-accessories__unavailable';
              badge.textContent = this.strings.unavailableLabel; (media || label).append(badge);
            }
            card.append(label);
            if (tooltipParts.length) {
              card.classList.add('desky-accessories__card--tooltip');
              const info = document.createElement('button');
              info.type = 'button'; info.className = 'desky-accessories__info'; info.textContent = 'i';
              info.setAttribute('aria-label', this.strings.details + ' ' + entry.title);
              const content = tooltipParts.join(' ');
              info.addEventListener('pointerenter', event => {
                if (event.pointerType === 'mouse') this.openTooltip(info, content);
              });
              info.addEventListener('pointerleave', () => this.scheduleTooltipClose());
              info.addEventListener('focus', () => this.openTooltip(info, content));
              info.addEventListener('blur', () => this.closeTooltip());
              info.addEventListener('click', () => {
                if (this.tooltipTrigger === info && this.tooltipPinned) this.closeTooltip();
                else { this.openTooltip(info, content); this.tooltipPinned = true; }
              });
              card.append(info);
            }
            row.append(card);
          });
          fieldset.append(row);
          // Summary is only needed when the label lives in the tooltip. If price is also
          // tooltip-only, the main/combined price already reflects the selection.
          if (group.labelDisplay === 'tooltip' && group.priceDisplay !== 'tooltip') {
            const selected = entries.find(entry => entry.id === this.selections[group.id]);
            if (selected) {
              const summary = document.createElement('p'); summary.className = 'desky-accessories__selected';
              summary.textContent = this.strings.selected + ' ' + selected.label;
              fieldset.append(summary);
            }
          }
        }
        const selectionError = core.validate(this.data, this.variant, this.selections).find(e => e.group === group.id &&
          (e.message !== 'required' || this.attempted));
        if (selectionError || this.confirmations.has(group.id)) {
          const message = document.createElement('p'); message.className = 'desky-accessories__error';
          message.id = 'AccessoryError-' + this.dataset.sectionId + '-' + group.id;
          message.textContent = this.confirmations.has(group.id) ? this.strings.priceChanged : this.strings[selectionError.message];
          fieldset.append(message);
          fieldset.querySelectorAll('select,input').forEach(input => {
            input.setAttribute('aria-describedby', message.id); input.setAttribute('aria-invalid', 'true');
          });
          if (this.confirmations.has(group.id)) {
            const confirm = document.createElement('button'); confirm.type = 'button'; confirm.dataset.accessoryConfirm = group.id;
            confirm.textContent = this.strings.confirmPrice; fieldset.append(confirm);
          }
        }
        container.append(fieldset);
      }
      this.querySelector('[data-accessory-total]').textContent = this.variant ? this.format(total) : '—';
      this.querySelector('.desky-accessories__total').hidden = productSoldOut || this.syncMainPrice(total);
      if (this.data.error) this.showError(this.strings.configuration);
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    }
    async submit() {
      if (this.busy || this.locked) return;
      this.attempted = true;
      // Resolve from the actual native selectors; never submit a stale hidden variant ID.
      const picker = this.section.querySelector('variant-selects,variant-radios');
      if (picker) {
        picker.updateOptions(); picker.updateMasterId();
        this.refresh(picker.currentVariant || null, true);
      } else this.refresh();
      const errors = core.validate(this.data, this.variant, this.selections);
      if (errors.length || this.confirmations.size) {
        this.render();
        const first = this.querySelector('[aria-invalid="true"]');
        if (first) { first.focus(); first.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        else { this.showError(this.strings[errors[0]?.message] || this.strings.required); this.error.focus(); }
        return;
      }
      const quantity = Number(this.form.elements.quantity?.value || 1);
      if (!Number.isSafeInteger(quantity) || quantity < 1) { this.showError(this.strings.quantity); return; }
      const properties = {};
      new FormData(this.form).forEach((value, key) => {
        const match = /^properties\[([^\]]+)\]$/.exec(key);
        if (match && (!match[1].startsWith('_') || match[1] === '_preorder') && typeof value === 'string') properties[match[1]] = value;
      });
      const configuration = crypto.randomUUID();
      const items = core.buildItems(this.data, this.variant, this.selections, quantity, configuration, properties);
      this.busy = true; this.showError('');
      this.render();
      this.form.querySelector('[name="add"]').disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      try {
        await window.DeskyAccessoryCart.add(items, configuration);
        this.status.textContent = this.strings.added;
      } catch (error) {
        this.locked = Boolean(error.uncertain);
        this.showError((error.message || this.strings.cartError) + (this.locked ? ' ' + this.strings.reviewCart : ''));
        this.error.focus();
      } finally {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        this.refresh();
      }
    }
  }
  customElements.define('desky-product-accessories', ProductAccessories);
})();
